import type { SxProps, Theme } from '@mui/material/styles'
import { type ChangeEvent, type FocusEvent, type ReactNode, useCallback, useEffect, useState } from 'react'
import { ids } from '../ids.ts'
import { Divider, InputAdornment, MuiIconButton, TextField } from './generic.tsx'
import { Icon } from './Icon.tsx'

type Value<TAllowEmpty extends boolean> = TAllowEmpty extends true ? number | undefined : number

type NumberInputProps<TAllowEmpty extends boolean> = {
	value: Value<TAllowEmpty>
	onBlur?: () => void
	onFocus?: () => void
	onChange?: (evt: { rawValue: Value<TAllowEmpty>; validatedValue: Value<TAllowEmpty>; valid: boolean }) => void
	onChangeCommited: (value: Value<TAllowEmpty>) => void
	allowEmpty?: TAllowEmpty
	min: number
	max: number
	step?: number
	disabled?: boolean
	required?: boolean
	label?: ReactNode
	error?: boolean
	helperText?: ReactNode
	startSymbol?: ReactNode
	endSymbol?: ReactNode
	decimals?: number
	showStepButtons?: boolean
	placeholder?: string
	sx?: SxProps<Theme>
	dataTa?: string
}

export const NumberInput = <TAllowEmpty extends boolean = false>({
	value,
	onBlur: _onBlur,
	onFocus: _onFocus,
	onChange,
	onChangeCommited,
	min,
	max,
	step = 1,
	disabled = false,
	required = false,
	label,
	error,
	helperText,
	startSymbol,
	endSymbol,
	decimals = 0,
	showStepButtons = true,
	allowEmpty,
	placeholder,
	sx,
	dataTa,
}: NumberInputProps<TAllowEmpty>) => {
	const clampAndTruncate = useCallback((val: number) => Number(Math.max(min, Math.min(max, val)).toFixed(decimals)), [decimals, max, min])
	const [inputValue, setInputValue] = useState(allowEmpty && value == null ? '' : String(value))
	const [isInputFocused, setIsInputFocused] = useState(false)

	const commitValue = (newValue: string | number) => {
		if (typeof newValue === 'string' && isEmpty(newValue) && allowEmpty) {
			setInputValue('')
			onChangeCommited(undefined as Value<TAllowEmpty>)
			return
		}

		const safeNumber = safeParseFloat(newValue)
		if (safeNumber == null) {
			setInputValue(allowEmpty && value == null ? '' : String(value))
			return
		}

		const validatedValue = clampAndTruncate(safeNumber)
		setInputValue(String(validatedValue))
		onChangeCommited(validatedValue)
	}

	const onInput = (e: ChangeEvent<HTMLInputElement>) => {
		if (isValidInput(e.target.value)) {
			setInputValue(e.target.value)

			if (onChange) {
				if (isEmpty(e.target.value) && allowEmpty && value != null) {
					onChange({ rawValue: undefined as Value<TAllowEmpty>, validatedValue: undefined as Value<TAllowEmpty>, valid: true })
					return
				}

				const safeNumber = safeParseFloat(e.target.value)
				if (safeNumber != null) {
					const validatedValue = clampAndTruncate(safeNumber)
					onChange({ rawValue: safeNumber, validatedValue, valid: safeNumber >= min && safeNumber <= max })
				}
			}
		}
	}

	const onFocus = () => {
		setIsInputFocused(true)
		_onFocus?.()
	}

	const onBlur = (e: FocusEvent<HTMLInputElement>) => {
		setIsInputFocused(false)
		commitValue(e.target.value)

		_onBlur?.()
	}

	const increment = () => value != null && commitValue(value + step)
	const decrement = () => value != null && commitValue(value - step)

	useEffect(() => {
		if (!isInputFocused) {
			setInputValue(allowEmpty && value == null ? '' : String(value))
		}
	}, [allowEmpty, isInputFocused, value])

	return (
		<TextField
			data-ta={dataTa}
			type='text'
			value={inputValue}
			onChange={onInput}
			onFocus={onFocus}
			onBlur={onBlur}
			error={error}
			helperText={helperText}
			label={label}
			placeholder={placeholder}
			required={required}
			size='small'
			disabled={disabled}
			fullWidth
			sx={sx}
			slotProps={{
				input: {
					startAdornment: startSymbol && <InputAdornment position='start'>{startSymbol}</InputAdornment>,
					endAdornment: (
						<InputAdornment position='end' sx={{ gap: 0.5 }}>
							{endSymbol}
							{showStepButtons && (
								<>
									<MuiIconButton
										data-ta={ids.formLayout.minusBtn}
										size='xsmall'
										color='secondary'
										onClick={decrement}
										disabled={disabled || value === undefined || value <= min}
									>
										<Icon name='Minus' />
									</MuiIconButton>
									<Divider orientation='vertical' flexItem />
									<MuiIconButton
										data-ta={ids.formLayout.plusBtn}
										size='xsmall'
										edge='end'
										color='secondary'
										onClick={increment}
										disabled={disabled || value === undefined || value >= max}
									>
										<Icon name='Plus' />
									</MuiIconButton>
								</>
							)}
						</InputAdornment>
					),
				},
			}}
		/>
	)
}

const isEmpty = (value: string) => value.trim().length === 0
const isValidInput = (value: string) => /^([+-]?((\d*(\d\.\d*)?)))?$/.test(value)
const safeParseFloat = (value: string | number) => {
	const parsed = Number.parseFloat(String(value))
	return Number.isNaN(parsed) ? undefined : parsed
}
